import { useEffect, useRef, useState } from "react";
import adhsImg from "@/assets/adhs-diagnostik.webp";
import kostenImg from "@/assets/adhs-kosten.webp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const ADHSSection = () => {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="adhs" ref={ref} className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Image placeholder */}
          <div
            className={`min-w-0 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <img src={adhsImg} alt="AD(H)S-Diagnostik – Person mit Konzentrationsschwierigkeiten" className="rounded-lg object-cover w-full" width={800} height={600} loading="lazy" />
          </div>

          {/* Text */}
          <div
            className={`min-w-0 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-primary mb-2">Diagnostik</p>
            <h2 className="font-serif text-2xl md:text-4xl font-bold text-foreground mb-6 hyphens-auto" lang="de">
              AD(H)S‑Diagnostik in unserer Praxis
            </h2>
            <div className="space-y-4 font-sans text-sm text-muted-foreground leading-relaxed">
              <p>
                Liegt der Verdacht auf eine Aufmerksamkeitsdefizit‑ / Hyperaktivitätsstörung (AD(H)S) vor, unterstützen wir Sie zeitnah und sorgfältig bei der diagnostischen Abklärung.
              </p>
              <p>
                Unsere AD(H)S‑Diagnostik erfolgt nach aktuellen wissenschaftlichen Leitlinien und orientiert sich an hohen fachlichen Standards. Ziel ist es, ein umfassendes und differenziertes Bild Ihrer individuellen Symptomatik zu erhalten.
              </p>
              <p>
                Die Diagnostik umfasst mehrere strukturierte Gespräche sowie den Einsatz wissenschaftlich fundierter Fragebögen und Testverfahren. Dabei berücksichtigen wir sowohl aktuelle Beschwerden als auch mögliche Hinweise aus der Kindheit und bisherigen Lebensgeschichte.
              </p>
              <p>
                Nach sorgfältiger Auswertung aller erhobenen Informationen erhalten Sie eine verständliche Rückmeldung zu den Ergebnissen. Zusätzlich erstellen wir einen ausführlichen schriftlichen Befundbericht, der die diagnostischen Ergebnisse nachvollziehbar zusammenfasst.
              </p>
              <p>
                Im Anschluss beraten wir Sie individuell zu möglichen nächsten Schritten und besprechen gemeinsam geeignete Behandlungsoptionen, falls sich ein Unterstützungsbedarf ergibt.
              </p>
              <p>
                <Dialog>
                  <DialogTrigger asChild>
                    <button
                      type="button"
                      className="text-primary underline hover:text-accent transition-colors font-medium"
                    >
                      Kosten ansehen
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="font-serif text-xl">
                        Übersicht des Leistungsangebotes und Kosten der AD(H)S-Diagnostik
                      </DialogTitle>
                    </DialogHeader>
                    <img
                      src={kostenImg}
                      alt="Übersicht des Leistungsangebotes und Kosten der AD(H)S-Diagnostik bei Erwachsenen"
                      className="w-full h-auto rounded-md"
                    />
                    <a
                      href="/ADHS_Leistungs-und_Kostenuebersicht.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-accent font-medium"
                    >
                      Kostenübersicht als PDF öffnen
                    </a>
                  </DialogContent>
                </Dialog>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ADHSSection;
